import axios from "axios";

function readTenantSubdomain() {
  const envTenant = String(process.env.REACT_APP_TENANT_SUBDOMAIN || "").trim().toLowerCase();
  const host = String(window.location.hostname || "").toLowerCase();
  const firstPart = host.split(".")[0];
  if (/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(firstPart) && !["localhost", "www", "api"].includes(firstPart) && host.split(".").length >= 3 && !/^(?:\d{1,3}\.){3}\d{1,3}$/.test(host)) return firstPart;

  try {
    const storedTenant = JSON.parse(localStorage.getItem("tenant") || "null");
    const storedSubdomain = String(storedTenant?.subdomain || "").trim().toLowerCase();
    if (/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(storedSubdomain)) return storedSubdomain;
  } catch {
    // localStorage non leggibile: continuo senza header tenant.
  }

  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(envTenant) ? envTenant : "";
}

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || "", withCredentials: true });
let csrfToken = "";
let csrfPromise = null;

const CATALOG_CACHE_TTL_MS = 30 * 1000;
const catalogItemsCache = new Map();

function catalogCacheKey(endpoint, params = {}) {
  const tenant = readTenantSubdomain() || "default";
  const normalized = Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
  return `${tenant}:${endpoint}?${normalized}`;
}

function invalidateCatalogCache(endpoint = "") {
  [...catalogItemsCache.keys()].forEach((key) => {
    const endpointToken = `:${endpoint}?`;
    if (!endpoint || key.includes(endpointToken)) catalogItemsCache.delete(key);
  });
}

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = api.get("/api/csrf").then((res) => {
      csrfToken = res?.data?.csrfToken || "";
      return csrfToken;
    }).finally(() => {
      csrfPromise = null;
    });
  }
  return csrfPromise;
}

api.interceptors.request.use(async (config) => {
  const tenantSubdomain = readTenantSubdomain();
  if (tenantSubdomain) config.headers["X-Vesta-Tenant"] = tenantSubdomain;

  const method = String(config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = await ensureCsrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error?.response?.status === 403 && error?.response?.data?.code === "CSRF_INVALID" && !error.config?._csrfRetry) {
      csrfToken = "";
      error.config._csrfRetry = true;
      const token = await ensureCsrfToken();
      if (token) error.config.headers["X-CSRF-Token"] = token;
      return api.request(error.config);
    }
    return Promise.reject(error);
  }
);

export async function loginUser(payload) {
  const res = await api.post("/api/login", payload);
  invalidateCatalogCache();
  return res.data;
}
export async function logoutUser() {
  const res = await api.post("/api/logout");
  csrfToken = "";
  invalidateCatalogCache();
  return res.data;
}
export async function me() {
  const res = await api.get("/api/me");
  return res.data;
}
export default api;

export async function getDashboardSummary(params = {}) {
  const res = await api.get("/api/dashboard-summary", { params });
  return res.data;
}

export async function getUtilityReport(type, slug, params = {}) {
  const res = await api.get(`/api/utility-reports/${type}/${slug}`, { params });
  return res.data;
}

export async function getDefaultNotes(params = {}) {
  const res = await api.get("/api/default-notes", { params });
  return res.data;
}

export async function createDefaultNote(payload) {
  const res = await api.post("/api/default-notes", payload);
  invalidateCatalogCache("default-notes");
  return res.data;
}

export async function updateDefaultNote(id, payload) {
  const res = await api.put(`/api/default-notes/${id}`, payload);
  invalidateCatalogCache("default-notes");
  return res.data;
}

export async function deleteDefaultNote(id, version) {
  const data = version === undefined || version === null ? undefined : { __v: version };
  const res = await api.delete(`/api/default-notes/${id}`, data ? { data } : undefined);
  invalidateCatalogCache("default-notes");
  return res.data;
}


export async function getBookings(params = {}) {
  const res = await api.get("/api/bookings", { params });
  return res.data;
}

export async function getLastBookingGuests(customerId) {
  const res = await api.get(`/api/bookings/customer/${customerId}/last-guests`);
  return res.data;
}

export async function createBooking(payload) {
  const res = await api.post("/api/bookings", payload);
  return res.data;
}

export async function updateBooking(id, payload) {
  const res = await api.put(`/api/bookings/${id}`, payload);
  return res.data;
}

export async function updateBookingPlanning(id, payload) {
  const res = await api.patch(`/api/bookings/${id}/planning`, payload);
  return res.data;
}

export async function confirmBooking(id) {
  const res = await api.patch(`/api/bookings/${id}/confirm`);
  return res.data;
}

export async function deleteBooking(id) {
  const res = await api.delete(`/api/bookings/${id}`);
  return res.data;
}

export async function getRooms(params = {}) {
  const res = await api.get("/api/rooms", { params });
  return res.data;
}

export async function createRoom(payload) {
  const res = await api.post("/api/rooms", payload);
  return res.data;
}

export async function updateRoom(id, payload) {
  const res = await api.put(`/api/rooms/${id}`, payload);
  return res.data;
}

export async function deleteRoom(id) {
  const res = await api.delete(`/api/rooms/${id}`);
  return res.data;
}

export async function createRoomMaintenance(roomId, payload) {
  const res = await api.post(`/api/rooms/${roomId}/maintenances`, payload);
  return res.data;
}

export async function updateRoomMaintenance(roomId, maintenanceId, payload) {
  const res = await api.put(`/api/rooms/${roomId}/maintenances/${maintenanceId}`, payload);
  return res.data;
}

export async function deleteRoomMaintenance(roomId, maintenanceId) {
  const res = await api.delete(`/api/rooms/${roomId}/maintenances/${maintenanceId}`);
  return res.data;
}

export async function createRoomCleaning(roomId, payload) {
  const res = await api.post(`/api/rooms/${roomId}/cleanings`, payload);
  return res.data;
}

export async function updateRoomCleaning(roomId, cleaningId, payload) {
  const res = await api.put(`/api/rooms/${roomId}/cleanings/${cleaningId}`, payload);
  return res.data;
}

export async function deleteRoomCleaning(roomId, cleaningId) {
  const res = await api.delete(`/api/rooms/${roomId}/cleanings/${cleaningId}`);
  return res.data;
}


export async function getCustomers(params = {}) {
  const res = await api.get("/api/customers", { params });
  return res.data;
}

export async function createCustomer(payload) {
  const res = await api.post("/api/customers", payload);
  return res.data;
}

export async function updateCustomer(id, payload) {
  const res = await api.put(`/api/customers/${id}`, payload);
  return res.data;
}

export async function lookupItalianVatNumber(vatNumber) {
  const res = await api.post("/api/customers/vat-lookup", { vatNumber });
  return res.data;
}


export async function getSites(params = {}) {
  const res = await api.get("/api/sites", { params });
  return res.data;
}

export async function createSite(payload) {
  const res = await api.post("/api/sites", payload);
  return res.data;
}

export async function updateSite(id, payload) {
  const res = await api.put(`/api/sites/${id}`, payload);
  return res.data;
}

export async function deleteSite(id) {
  const res = await api.delete(`/api/sites/${id}`);
  return res.data;
}



export async function getCreditCardCircuits(params = {}) {
  const res = await api.get("/api/creditcard-circuits", { params });
  return res.data;
}

export async function deleteCustomer(id) {
  const res = await api.delete(`/api/customers/${id}`);
  return res.data;
}

export async function getCatalogDefinitions() {
  const res = await api.get("/api/catalogs");
  return res.data;
}

export async function getCatalogItems(endpoint, params = {}) {
  // I servizi dipendono dalle sedi dell'utente: non riusare risultati cache
  // dopo la modifica delle abilitazioni durante la stessa sessione.
  if (endpoint === "hotel-services") {
    const res = await api.get(`/api/catalogs/${endpoint}`, { params });
    return res.data;
  }
  const key = catalogCacheKey(endpoint, params);
  const cached = catalogItemsCache.get(key);
  const now = Date.now();
  if (cached?.value && cached.expiresAt > now) return cached.value;
  if (cached?.promise) return cached.promise;

  const promise = api.get(`/api/catalogs/${endpoint}`, { params })
    .then((res) => {
      catalogItemsCache.set(key, { value: res.data, expiresAt: Date.now() + CATALOG_CACHE_TTL_MS });
      return res.data;
    })
    .catch((error) => {
      catalogItemsCache.delete(key);
      throw error;
    });

  catalogItemsCache.set(key, { promise, expiresAt: now + CATALOG_CACHE_TTL_MS });
  return promise;
}

export async function createCatalogItem(endpoint, payload) {
  const res = await api.post(`/api/catalogs/${endpoint}`, payload);
  invalidateCatalogCache(endpoint);
  return res.data;
}

export async function updateCatalogItem(endpoint, id, payload) {
  const res = await api.put(`/api/catalogs/${endpoint}/${id}`, payload);
  invalidateCatalogCache(endpoint);
  return res.data;
}

export async function deleteCatalogItem(endpoint, id, version) {
  const data = version === undefined || version === null ? undefined : { __v: version };
  const res = await api.delete(`/api/catalogs/${endpoint}/${id}`, data ? { data } : undefined);
  invalidateCatalogCache(endpoint);
  return res.data;
}

export async function swapCatalogItemOrder(endpoint, payload) {
  const res = await api.post(`/api/catalogs/${endpoint}/order-swap`, payload);
  return res.data;
}

export async function getUserSites() {
  const res = await api.get("/api/users/sites");
  return res.data;
}

export async function getUsers(params = {}) {
  const res = await api.get("/api/users", { params });
  return res.data;
}

export async function createUser(payload) {
  const res = await api.post("/api/users", payload);
  return res.data;
}

export async function updateUser(id, payload) {
  const res = await api.put(`/api/users/${id}`, payload);
  return res.data;
}

export async function deleteUser(id) {
  const res = await api.delete(`/api/users/${id}`);
  return res.data;
}

export async function getRoles(params = {}) {
  const res = await api.get("/api/roles", { params });
  return res.data;
}

export async function createRole(payload) {
  const res = await api.post("/api/roles", payload);
  return res.data;
}

export async function updateRole(id, payload) {
  const res = await api.put(`/api/roles/${id}`, payload);
  return res.data;
}

export async function deleteRole(id, version) {
  const data = version === undefined || version === null ? undefined : { __v: version };
  const res = await api.delete(`/api/roles/${id}`, data ? { data } : undefined);
  return res.data;
}


export async function searchGeneral(params = {}) {
  const res = await api.get("/api/search", { params });
  return res.data;
}

export async function getNotifications(params = {}) {
  const res = await api.get("/api/notifications", { params });
  return res.data;
}

export async function setNotificationRead(id, read = true) {
  const res = await api.post(`/api/notifications/${encodeURIComponent(id)}/read`, { read });
  return res.data;
}

export async function markAllNotificationsRead() {
  const res = await api.post("/api/notifications/read-all");
  return res.data;
}

export async function getServiceCatalog(params = {}) {
  const res = await api.get("/api/services", { params });
  return res.data;
}

export async function getFiscalDocumentTypes() {
  const res = await api.get("/api/fiscal-document-types");
  return res.data;
}

export async function getBookingAccounts(params = {}) {
  const res = await api.get("/api/booking-accounts", { params });
  return res.data;
}

export async function createBookingAccountService(bookingId, payload) {
  const res = await api.post(`/api/booking-accounts/${bookingId}/services`, payload);
  return res.data;
}

export async function updateBookingAccountService(bookingId, serviceRowId, payload) {
  const res = await api.put(`/api/booking-accounts/${bookingId}/services/${serviceRowId}`, payload);
  return res.data;
}

export async function deleteBookingAccountService(bookingId, serviceRowId) {
  const res = await api.delete(`/api/booking-accounts/${bookingId}/services/${serviceRowId}`);
  return res.data;
}

export async function issueBookingCheckoutDocument(bookingId, payload) {
  const res = await api.post(`/api/booking-accounts/${bookingId}/checkout-document`, payload);
  return res.data;
}

export async function issueBookingAdvanceDocument(bookingId, paymentIndex, payload) {
  const res = await api.post(`/api/booking-accounts/${bookingId}/payments/${paymentIndex}/advance-document`, payload);
  return res.data;
}

export async function cancelBookingAdvanceDocument(bookingId, paymentIndex) {
  const res = await api.delete(`/api/booking-accounts/${bookingId}/payments/${paymentIndex}/advance-document`);
  return res.data;
}

export async function issueBookingAdvanceReversalDocument(bookingId, paymentIndex, payload) {
  const res = await api.post(`/api/booking-accounts/${bookingId}/payments/${paymentIndex}/advance-reversal-document`, payload);
  return res.data;
}

export async function cancelBookingAdvanceReversalDocument(bookingId, paymentIndex, documentId) {
  const res = await api.delete(`/api/booking-accounts/${bookingId}/payments/${paymentIndex}/advance-reversal-document`, { data: { documentId } });
  return res.data;
}

export async function issueBookingCreditNote(bookingId, payload) {
  const res = await api.post(`/api/booking-accounts/${bookingId}/credit-note`, payload);
  return res.data;
}

export async function issueBookingReversalDocument(bookingId, payload) {
  const res = await api.post(`/api/booking-accounts/${bookingId}/reversal-document`, payload);
  return res.data;
}

export async function cancelBookingReversalDocument(bookingId, documentId) {
  const res = await api.delete(`/api/booking-accounts/${bookingId}/reversal-document`, { data: { documentId } });
  return res.data;
}

export async function cancelBookingCheckoutDocument(bookingId, documentId) {
  const res = await api.delete(`/api/booking-accounts/${bookingId}/checkout-document`, { data: { documentId } });
  return res.data;
}

export async function getCompanyProfile() {
  const res = await api.get("/api/company-profile");
  return res.data;
}

export async function updateCompanyProfile(payload) {
  const res = await api.put("/api/company-profile", payload);
  return res.data;
}

export async function getCompanyBankCatalog(abi = "") {
  const res = await api.get("/api/company-profile/bank-catalog", { params: abi ? { abi } : undefined });
  return res.data;
}

export async function getCompanyBankLookup(abi, cab) {
  const res = await api.get("/api/company-profile/bank-lookup", { params: { abi, cab } });
  return res.data;
}

export async function uploadCompanyLogo(file) {
  const res = await api.put("/api/company-profile/logo", file, {
    headers: { "Content-Type": file?.type || "application/octet-stream" },
  });
  return res.data;
}

export async function uploadCompanyLogoCrop(file, position = {}) {
  const res = await api.put("/api/company-profile/logo-crop", file, {
    params: { x: position.x, y: position.y, z: position.zoom },
    headers: { "Content-Type": file?.type || "image/png" },
  });
  return res.data;
}

export async function deleteCompanyLogo() {
  const res = await api.delete("/api/company-profile/logo");
  return res.data;
}

export async function getBookingDocumentNumberPreview(params = {}) {
  const res = await api.get("/api/booking-accounts/document-number-preview", { params });
  return res.data;
}


export async function getQuotes(params = {}) {
  const res = await api.get("/api/quotes", { params });
  return res.data;
}

export async function createQuote(payload) {
  const res = await api.post("/api/quotes", payload);
  return res.data;
}

export async function updateQuote(id, payload) {
  const res = await api.put(`/api/quotes/${id}`, payload);
  return res.data;
}

export async function getFreeInvoices(params = {}) {
  const res = await api.get("/api/free-invoices", { params });
  return res.data;
}

export async function createFreeInvoice(payload) {
  const res = await api.post("/api/free-invoices", payload);
  return res.data;
}

export async function cancelFreeInvoice(id) {
  const res = await api.delete(`/api/free-invoices/${id}`);
  return res.data;
}

export async function getFiscalDocuments(params = {}) {
  const res = await api.get("/api/fiscal-documents", { params });
  return res.data;
}

export async function getFiscalXmlArchive(params = {}) {
  const res = await api.get("/api/fiscal-documents/xml-archive", { params });
  return res.data;
}

export async function getFiscalDocumentXml(id) {
  const res = await api.get(`/api/fiscal-documents/${id}/xml`, { responseType: "blob" });
  return res.data;
}

export async function createFiscalShareLink(id) {
  const res = await api.post(`/api/fiscal-documents/${id}/share-link`);
  return res.data;
}

export async function sendFiscalDocumentEmail(id, to) {
  const res = await api.post(`/api/fiscal-documents/${id}/send-email`, { to });
  return res.data;
}
